// DenKind upsells (20 Oct 2026): the live total inside Add to basket, the tick box above it, the
// "Make it a Den set" rows and pairing cards below it, the matched card in the basket and the
// basket page gift note. Every add-on line carries a hidden line property _source (pdp-tick,
// den-set-box, pdp-card, drawer-card) so orders show where the add came from.
// Prices follow Shopify's rule for the "Den set: 10% off two pieces" automatic discount, measured
// in real baskets: once two or more Den pieces are in the basket, 10% comes off each unit,
// rounded down to the penny (£32.99 -> £3.29 off, £27.99 -> £2.79 off).
import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) => `£${(cents / 100).toFixed(2)}`;
const off = (cents) => Math.floor(cents / 10);
const priced = (units) => {
  const den = units.reduce((n, u) => n + (u.den ? u.qty : 0), 0);
  let total = 0;
  let saving = 0;
  for (const u of units) {
    const s = den >= 2 && u.den ? off(u.price) : 0;
    total += (u.price - s) * u.qty;
    saving += s * u.qty;
  }
  return { total, saving };
};
const json = (el, key) => {
  try {
    return JSON.parse((el && el.dataset[key]) || '{}');
  } catch (error) {
    return {};
  }
};
const setText = (el, text) => {
  if (el && el.textContent !== text) el.textContent = text;
};

// ---------------------------------------------------------------- adding in place
const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

const addLines = async (lines, source, origin) => {
  const deferred = CartLinesUpdateEvent.createPromise();
  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const added = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((l) => ({ id: Number(l.id), quantity: l.quantity, properties: { _source: source } })),
        sections: sectionIds().join(','),
      }),
    }).then((response) => response.json());
    if (added.status) throw new Error(added.description || added.message || 'Could not add');
    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'dk-upsells', itemCount: lines.reduce((n, l) => n + l.quantity, 0), sections: added.sections, didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
};

// ---------------------------------------------------------------- product page state
const productForm = () =>
  document.querySelector('.product-details product-form-component') || document.querySelector('product-form-component');
const mainVariant = () => productForm()?.querySelector('input[name="id"]')?.value;
const mainQty = () => Math.max(1, Number(productForm()?.querySelector('input[name="quantity"]')?.value) || 1);
const mainInfo = () => {
  const el = document.querySelector('[data-dk-main]');
  if (!el) return null;
  const price = json(el, 'dkPrices')[mainVariant()];
  return price ? { price, den: el.dataset.den === '1' } : null;
};

let tickWanted = false;
const tickState = () => {
  const el = document.querySelector('[data-dk-tick]');
  if (!el) return null;
  const input = el.querySelector('[data-dk-tick-input]');
  const vid = mainVariant();
  const mainPrice = json(el, 'dkPrices')[vid];
  if (el.dataset.kind === 'blanket') {
    const id = json(el, 'dkMap')[vid];
    const item = json(el, 'dkItems')[id];
    if (!item) return null;
    return { el, input, kind: 'blanket', id, price: item.price, size: item.size, mainPrice };
  }
  if (el.dataset.kind === 'second' && mainPrice) return { el, input, kind: 'second', id: vid, price: mainPrice, mainPrice };
  return null;
};

const refreshTick = (s) => {
  if (!s) return;
  if (s.input.checked !== tickWanted) s.input.checked = tickWanted;
  const qty = mainQty();
  const label = s.el.querySelector('[data-dk-tick-label]');
  const note = s.el.querySelector('[data-dk-tick-note]');
  if (s.kind === 'blanket') {
    setText(label, `Add the Fluffy Blanket, ${s.size}, ${money(s.price)}`);
    const saving = priced([{ price: s.mainPrice, qty, den: true }, { price: s.price, qty: 1, den: true }]).saving;
    setText(note, `With this bed, 10% comes off ${qty > 1 ? 'every piece' : 'both'}: ${money(saving)} off in your basket.`);
  } else {
    const saving = priced([{ price: s.price, qty: qty + 1, den: true }]).saving;
    setText(note, `Same colour and size as above. You save ${money(saving)} on the ${qty > 1 ? 'robes' : 'two'}.`);
  }
};

const refreshButton = (s) => {
  const main = mainInfo();
  const button = document.querySelector('.product-details .add-to-cart-button, .product-details button[name="add"]');
  if (!main || !button || button.disabled) return;
  const qty = mainQty();
  const units = [{ price: main.price, qty, den: main.den }];
  const ticked = !!(s && s.input.checked);
  if (ticked) units.push({ price: s.price, qty: 1, den: true });
  const { total, saving } = priced(units);
  const label = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim());
  if (label) {
    const now = label.nodeValue.trim();
    if (ticked && now !== 'Add both to basket') {
      button.dataset.dkLabel = now;
      label.nodeValue = 'Add both to basket';
    } else if (!ticked && now === 'Add both to basket') {
      label.nodeValue = button.dataset.dkLabel || 'Add to basket';
    }
  }
  let span = button.querySelector('.dk-btn-total');
  if (!span) {
    span = document.createElement('span');
    span.className = 'dk-btn-total';
    button.insertBefore(span, button.querySelector('.add-to-cart__added'));
  }
  setText(span, money(total));
  const container = button.closest('add-to-cart-component') || button;
  let note = document.querySelector('.dk-btn-note');
  if (saving > 0) {
    if (!note) {
      note = document.createElement('p');
      note.className = 'dk-btn-note';
      container.insertAdjacentElement('afterend', note);
    }
    setText(note, `Den set: 10% off ${ticked && qty === 1 ? 'both' : 'each piece'}, ${money(saving)} taken off in your basket.`);
  } else if (note) {
    note.remove();
  }
};

const refreshRows = () => {
  const main = mainInfo();
  document.querySelectorAll('[data-dk-den]').forEach((box) => {
    const extra = box.querySelector('[data-dk-den-extra]');
    box.querySelectorAll('[data-dk-den-row]').forEach((row) => {
      const option = row.querySelector('select')?.selectedOptions[0];
      const out = row.querySelector('[data-dk-row-total]');
      if (!main || !option || !out) return;
      const units = [{ price: main.price, qty: 1, den: true }, { price: Number(option.dataset.price), qty: 1, den: true }];
      if (extra && extra.checked) units.push({ price: Number(extra.dataset.price), qty: 1, den: false });
      setText(out, money(priced(units).total));
    });
  });
  document.querySelectorAll('[data-dk-card]').forEach((card) => {
    const option = card.querySelector('select')?.selectedOptions[0];
    if (!option) return;
    const saving = card.querySelector('[data-dk-card-saving]');
    const price = card.querySelector('[data-dk-card-price]');
    if (price && option.dataset.price) setText(price, money(Number(option.dataset.price)));
    if (saving && card.hasAttribute('data-dk-den-card') && main) {
      setText(saving, money(off(Number(option.dataset.price)) + off(main.price)));
    } else if (saving && option.dataset.saving) {
      setText(saving, money(Number(option.dataset.saving)));
    }
  });
};

let queued = false;
const refresh = () => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    const s = tickState();
    refreshTick(s);
    refreshButton(s);
    refreshRows();
  });
};

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.matches('[data-dk-tick-input]')) tickWanted = event.target.checked;
  refresh();
});
document.addEventListener('input', refresh);
document.addEventListener('click', () => setTimeout(refresh, 60));
new MutationObserver(refresh).observe(document.documentElement, { childList: true, subtree: true });
// Horizon changes the variant and quantity inputs by property, which no observer sees.
let last = '';
setInterval(() => {
  const now = `${mainVariant()}|${mainQty()}`;
  if (now !== last) {
    last = now;
    refresh();
  }
}, 400);
refresh();

// ---------------------------------------------------------------- tick box: add it with the main item
document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component') || form.dataset.dkPass === '1') return;
    const s = tickState();
    if (!s || !s.input.checked || !s.id) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      const added = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(s.id), quantity: 1, properties: { _source: 'pdp-tick' } }] }),
      }).then((response) => response.json());
      if (added.status) throw new Error(added.description || 'Could not add');
      tickWanted = false;
    } catch (error) {
      /* the main item still goes in; the box stays ticked so the shopper sees what happened */
    }
    form.dataset.dkPass = '1';
    try {
      form.requestSubmit();
    } finally {
      delete form.dataset.dkPass;
    }
    refresh();
  },
  true
);

// ---------------------------------------------------------------- Den set rows and one-tap cards
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  let lines;
  let source;
  if (form.matches('[data-dk-den-row]')) {
    event.preventDefault();
    const box = form.closest('[data-dk-den]');
    lines = [
      { id: mainVariant(), quantity: 1 },
      { id: new FormData(form).get('id'), quantity: 1 },
    ];
    const extra = box && box.querySelector('[data-dk-den-extra]');
    if (extra && extra.checked) lines.push({ id: extra.value, quantity: 1 });
    source = form.dataset.dkSource || 'den-set-box';
  } else if (form.matches('[data-dk-card]')) {
    event.preventDefault();
    lines = [{ id: new FormData(form).get('id'), quantity: 1 }];
    source = form.dataset.dkSource || 'pdp-card';
  } else {
    return;
  }
  const button = form.querySelector('button[type="submit"]');
  if (button) button.disabled = true;
  try {
    await addLines(lines.filter((l) => l.id), source, form);
  } catch (error) {
    if (button) button.textContent = 'Could not add, try again';
  } finally {
    if (button) button.disabled = false;
  }
});

// ---------------------------------------------------------------- basket page gift note
const gift = document.querySelector('[data-dk-gift-note]');
if (gift) {
  const saved = document.querySelector('[data-dk-gift-saved]');
  let timer;
  gift.addEventListener('input', () => {
    clearTimeout(timer);
    if (saved) saved.hidden = true;
    timer = setTimeout(async () => {
      try {
        await fetch(`${Theme.routes.cart_url}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: gift.value }),
        });
        if (saved) saved.hidden = false;
      } catch (error) {
        /* try again on the next keystroke */
      }
    }, 600);
  });
}
